import classNames from 'classnames';
import { FieldPath, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField, Select } from '@client/toolkit';

import { ECONOMY_CHIP_OFF_CLASS, ECONOMY_CHIP_OK_CLASS, ECONOMY_FIELD_SIZE } from '../economy-theme';

/** `Select` speaks strings, the form field a boolean — one mapping for every table. */
const ACTIVE = 'active';
const INACTIVE = 'inactive';

export interface EconomyActiveFieldProps<TFieldValues extends FieldValues> {
  /** i18n prefix of the owning table, e.g. `settings.groupAccounts`. */
  section: string;
  /** Interpolated into the label so each row's select is distinguishable. */
  row: string;
  name: FieldPath<TFieldValues>;
  active: boolean | undefined;
  editing: boolean;
}

/** The Aktiv column of an account table: a state chip when read-only, a select when editing. */
export function EconomyActiveField<TFieldValues extends FieldValues>({
  section,
  row,
  name,
  active,
  editing,
}: EconomyActiveFieldProps<TFieldValues>) {
  const { t } = useTranslation('economy');

  const activeLabel = t(`${section}.active`);
  const inactiveLabel = t(`${section}.inactive`);

  if (!editing) {
    return (
      <span
        className={classNames(
          'inline-block rounded-full px-3 py-1',
          active ? ECONOMY_CHIP_OK_CLASS : ECONOMY_CHIP_OFF_CLASS,
        )}
      >
        {active ? activeLabel : inactiveLabel}
      </span>
    );
  }

  return (
    <FormField<TFieldValues, FieldPath<TFieldValues>>
      name={name}
      render={({ field }) => (
        <Select
          {...field}
          value={field.value ? ACTIVE : INACTIVE}
          onChange={(event) => field.onChange(event.target.value === ACTIVE)}
          size={ECONOMY_FIELD_SIZE}
          aria-label={t(`${section}.rowFields.active`, { row })}
        >
          <option value={ACTIVE}>{activeLabel}</option>
          <option value={INACTIVE}>{inactiveLabel}</option>
        </Select>
      )}
    />
  );
}

export default EconomyActiveField;
